import type {
  CalendarEvent,
  CalendarInfo,
  Fact,
  Note,
  WeatherReport,
  GameId,
  GameReply,
  GameScores,
  GameState,
  Language,
  Mood,
  DeviceInfo,
  JsonSchema,
  PermissionLevel,
  PermissionPolicy,
  Routine,
  RoutineInput,
  RoutineRun,
} from '@elena/shared';
import { session } from './session';

export interface ServerStatus {
  version: string;
  model: string;
  devices: DeviceInfo[];
  server: {
    online: boolean;
    name: string;
    players: Array<{ id: number; name: string; ping: number }>;
    maxPlayers: number | null;
    hostname: string | null;
    resources: number | null;
    checkedAt: string;
  };
  budget: { month: string; usd: number; requests: number; limitUsd: number };
  voice: { enabled: boolean; name: string; provider?: 'elevenlabs' | 'azure' | null };
  /** This month's use of the free Azure Speech tier. */
  speech?: SpeechUsage;
  /** ADMIN LINE calls through the admin panel (older servers send none). */
  adminLine?: { configured: boolean; connected: boolean; phone: boolean; calls: number };
}

export interface SpeechUsage {
  month: string;
  stt: { usedSeconds: number; limitSeconds: number; available: boolean };
  tts: { usedChars: number; limitChars: number; available: boolean };
  /** ElevenLabs voice characters this month (older servers send none). */
  eleven?: { usedChars: number; limitChars: number; available: boolean };
  /** Seconds the paid recognizers heard this month. */
  paidStt?: Partial<Record<'soniox' | 'elevenlabs', number>>;
}

/** Who listens to the mic: a cloud recognizer, or the browser's own (Chrome / Edge). */
export type SttEngine = 'soniox' | 'elevenlabs' | 'azure' | 'browser';

/** How to listen; enabled: false = use the browser recognizer. */
export interface SttInfo {
  enabled: boolean;
  /** Missing on older servers: then it is Azure when enabled. */
  engine?: SttEngine;
  reason?: 'off' | 'limit' | 'token';
  token?: string;
  region?: string;
  /** Azure token as the fallback of a paid engine (null = no Azure). */
  azure?: { token: string; region: string } | null;
  phrases?: string[];
  usage: SpeechUsage;
}

export interface DictionaryEntry {
  id: string;
  heard: string;
  meant: string;
  hits: number;
  at: string;
}

export interface ActionMeta {
  id: string;
  description: string;
  target: 'pc' | 'server';
  risk: 'low' | 'medium' | 'high';
  params: JsonSchema;
}

export interface PermissionsResponse {
  policy: PermissionPolicy;
  matrix: Array<{ action: string; levels: Record<'pc' | 'phone' | 'web', PermissionLevel> }>;
}

export interface IntegrationsResponse {
  discord: { webhooks: Array<{ name: string; url: string }> };
  spotify: {
    configured: boolean;
    connected: boolean;
    user: string | null;
    connectedAt: string | null;
    redirectUri: string | null;
  };
  push: {
    devices: Array<{
      deviceId: string;
      name: string;
      platform: 'android' | 'ios';
      addedAt: string;
    }>;
  };
  youtube: { exactMatch: boolean };
  voice: { enabled: boolean; name: string };
  fivem: { name: string; address: string };
  publicUrl: string | null;
  bridge: { configured: boolean; url: string };
}

export interface NowPlaying {
  playing: boolean;
  title: string;
  artist: string;
  device: string | null;
}

export interface CommentatorSettings {
  enabled: boolean;
  ownerName: string;
  friends: string[];
  everyone: boolean;
  speak: boolean;
  push: boolean;
  discord: string;
  inGame: boolean;
  records: boolean;
}

export interface Reminder {
  id: string;
  text: string;
  at: string;
  createdAt: string;
  firedAt: string | null;
}

export interface StatsReport {
  days: number;
  samples: number;
  uptime: number | null;
  peak: { players: number; at: string } | null;
  average: number | null;
  hourly: Array<number | null>;
  bestRestartHour: { hour: number; average: number } | null;
  incidents: Array<{ from: string; to: string | null; minutes: number }>;
  daily: Array<{
    day: string;
    peak: number;
    average: number;
    offlineMinutes: number;
    uniquePlayers: number;
  }>;
  top: Array<{ name: string; minutes: number }>;
  series: Array<[number, number | null]>;
}

export interface ReportSettings {
  enabled: boolean;
  time: string;
  last: { day: string; sentAt: string; title: string; text: string } | null;
}

export interface BridgeState {
  configured: boolean;
  status: { version: string; players: number; resources: number; errors: number } | null;
  error?: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      authorization: `Bearer ${session.token() ?? ''}`,
      ...init.headers,
    },
  });
  if (!res.ok) {
    let message = `Greška ${res.status}`;
    try {
      message = ((await res.json()) as { error?: string }).error ?? message;
    } catch {
      // not JSON
    }
    throw new ApiError(res.status, message);
  }
  return (await res.json()) as T;
}

export const api = {
  stt: () => request<SttInfo>('/api/stt'),
  sttKey: (engine: 'soniox' | 'elevenlabs') =>
    request<{ engine: string; key: string; model: string }>('/api/stt/key', {
      method: 'POST',
      body: JSON.stringify({ engine }),
    }),
  sttUsage: (body: {
    seconds?: number;
    exhausted?: boolean;
    engine?: 'azure' | 'soniox' | 'elevenlabs';
  }) => request<SpeechUsage>('/api/stt/usage', { method: 'POST', body: JSON.stringify(body) }),
  dictionary: () => request<{ entries: DictionaryEntry[] }>('/api/speech/dictionary'),
  learnWord: (heard: string, meant: string) =>
    request<{ entries: DictionaryEntry[] }>('/api/speech/dictionary', {
      method: 'POST',
      body: JSON.stringify({ heard, meant }),
    }),
  forgetWord: (id: string) =>
    request<{ entries: DictionaryEntry[] }>(`/api/speech/dictionary/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  status: () => request<ServerStatus>('/api/status'),
  actions: () => request<{ actions: ActionMeta[] }>('/api/actions'),
  permissions: () => request<PermissionsResponse>('/api/permissions'),
  savePermissions: (policy: PermissionPolicy) =>
    request<PermissionsResponse>('/api/permissions', {
      method: 'PUT',
      body: JSON.stringify({ policy }),
    }),
  reset: () => request<{ ok: true }>('/api/reset', { method: 'POST' }),
  runAction: (id: string, params: Record<string, unknown>, origin: string) =>
    request<{ ok: boolean; message: string }>(`/api/actions/${encodeURIComponent(id)}/run`, {
      method: 'POST',
      body: JSON.stringify({ params, origin }),
    }),

  routines: () => request<{ routines: Routine[] }>('/api/routines'),
  createRoutine: (input: RoutineInput) =>
    request<{ routine: Routine }>('/api/routines', { method: 'POST', body: JSON.stringify(input) }),
  updateRoutine: (id: string, input: RoutineInput) =>
    request<{ routine: Routine }>(`/api/routines/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  deleteRoutine: (id: string) => request<{ ok: true }>(`/api/routines/${id}`, { method: 'DELETE' }),
  runRoutine: (id: string, origin: string) =>
    request<{ run: RoutineRun; text: string }>(`/api/routines/${id}/run`, {
      method: 'POST',
      body: JSON.stringify({ origin }),
    }),

  games: (deviceId: string) =>
    request<{
      state: GameState | null;
      scores: GameScores;
      games: Array<{ id: GameId; name: string; hint: string; rules: string }>;
    }>(`/api/games?deviceId=${encodeURIComponent(deviceId)}`),
  startGame: (deviceId: string, game: GameId) =>
    request<GameReply>('/api/games/start', {
      method: 'POST',
      body: JSON.stringify({ deviceId, game }),
    }),
  moveGame: (deviceId: string, move: string) =>
    request<GameReply>('/api/games/move', {
      method: 'POST',
      body: JSON.stringify({ deviceId, move }),
    }),
  stopGame: (deviceId: string) =>
    request<GameReply>('/api/games/stop', { method: 'POST', body: JSON.stringify({ deviceId }) }),
  setLanguage: (language: Language) =>
    request<{ language: Language; text: string }>('/api/language', {
      method: 'PUT',
      body: JSON.stringify({ language }),
    }),
  mood: () =>
    request<{ mood: Mood; moods: Array<{ id: Mood; label: string; hint: string }> }>('/api/mood'),
  setMood: (mood: Mood) =>
    request<{ mood: Mood; text: string }>('/api/mood', {
      method: 'PUT',
      body: JSON.stringify({ mood }),
    }),
  commentator: () => request<{ settings: CommentatorSettings; sample: string }>('/api/commentator'),
  saveCommentator: (settings: CommentatorSettings) =>
    request<{ settings: CommentatorSettings }>('/api/commentator', {
      method: 'PUT',
      body: JSON.stringify(settings),
    }),
  testCommentator: () =>
    request<{ text: string | null }>('/api/commentator/test', { method: 'POST', body: '{}' }),
  weather: (city?: string, day = 0) =>
    request<{ report: WeatherReport; home: string }>(
      `/api/weather?day=${day}${city ? `&city=${encodeURIComponent(city)}` : ''}`,
    ),
  setHomeCity: (city: string) =>
    request<{ home: string }>('/api/weather', { method: 'PUT', body: JSON.stringify({ city }) }),
  calendar: () => request<{ calendar: CalendarInfo }>('/api/calendar'),
  connectCalendar: (input: {
    url: string;
    username: string;
    password?: string;
    writeTo?: string;
  }) =>
    request<{ calendar: CalendarInfo }>('/api/calendar', {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  disconnectCalendar: () =>
    request<{ calendar: CalendarInfo }>('/api/calendar', { method: 'DELETE' }),
  calendarEvents: (days = 14, from?: string) =>
    request<{ events: CalendarEvent[]; configured: boolean }>(
      `/api/calendar/events?days=${days}${from ? `&from=${from}` : ''}`,
    ),
  addEvent: (input: {
    title: string;
    start: string;
    durationMinutes: number;
    location?: string;
    alertMinutes?: number;
  }) =>
    request<{ ok: boolean; message: string }>('/api/calendar/events', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  removeEvent: (href: string) =>
    request<{ ok: true }>(`/api/calendar/events?href=${encodeURIComponent(href)}`, {
      method: 'DELETE',
    }),
  notes: () => request<{ notes: Note[]; lists: string[] }>('/api/notes'),
  addNote: (text: string, list?: string) =>
    request<{ note: Note }>('/api/notes', {
      method: 'POST',
      body: JSON.stringify({ text, ...(list ? { list } : {}) }),
    }),
  updateNote: (id: string, patch: { text?: string; done?: boolean; list?: string }) =>
    request<{ note: Note }>(`/api/notes/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
  removeNote: (id: string) => request<{ ok: true }>(`/api/notes/${id}`, { method: 'DELETE' }),
  facts: () => request<{ facts: Fact[] }>('/api/memory'),
  addFact: (text: string) =>
    request<{ fact: Fact; added: boolean }>('/api/memory', {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),
  removeFact: (id: string) => request<{ ok: true }>(`/api/memory/${id}`, { method: 'DELETE' }),
  reminders: () => request<{ pending: Reminder[]; recent: Reminder[] }>('/api/reminders'),
  addReminder: (text: string, at: string) =>
    request<{ reminder: Reminder; text: string }>('/api/reminders', {
      method: 'POST',
      body: JSON.stringify({ text, at }),
    }),
  removeReminder: (id: string) =>
    request<{ ok: true }>(`/api/reminders/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  stats: (days: number) =>
    request<{ stats: StatsReport; summary: string }>(`/api/stats?days=${days}`),
  report: () => request<{ report: ReportSettings }>('/api/report'),
  saveReport: (enabled: boolean, time: string) =>
    request<{ report: ReportSettings }>('/api/report', {
      method: 'PUT',
      body: JSON.stringify({ enabled, time }),
    }),
  sendReport: () =>
    request<{ report: ReportSettings }>('/api/report/send', { method: 'POST', body: '{}' }),
  bridge: () => request<BridgeState>('/api/bridge/status'),
  integrations: () => request<IntegrationsResponse>('/api/integrations'),
  addWebhook: (name: string, url: string) =>
    request<{ webhooks: IntegrationsResponse['discord']['webhooks'] }>(
      '/api/integrations/discord',
      { method: 'POST', body: JSON.stringify({ name, url }) },
    ),
  removeWebhook: (name: string) =>
    request<{ webhooks: IntegrationsResponse['discord']['webhooks'] }>(
      `/api/integrations/discord/${encodeURIComponent(name)}`,
      { method: 'DELETE' },
    ),
  testWebhook: (name: string) =>
    request<{ message: string }>(`/api/integrations/discord/${encodeURIComponent(name)}/test`, {
      method: 'POST',
      body: '{}',
    }),
  spotifyConnect: () =>
    request<{ url: string }>('/api/integrations/spotify/connect', { method: 'POST', body: '{}' }),
  spotifyDisconnect: () => request<unknown>('/api/integrations/spotify', { method: 'DELETE' }),
  spotifyNow: () => request<{ now: NowPlaying | null }>('/api/integrations/spotify/now'),
  removePhone: (deviceId: string) =>
    request<{ ok: true }>(`/api/push/${encodeURIComponent(deviceId)}`, { method: 'DELETE' }),
  testPush: () => request<{ message: string }>('/api/push/test', { method: 'POST', body: '{}' }),

  async tts(text: string, format: 'mp3' | 'wav' = 'mp3'): Promise<Blob> {
    const res = await fetch('/api/tts', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${session.token() ?? ''}`,
      },
      body: JSON.stringify({ text, format }),
    });
    if (!res.ok) throw new ApiError(res.status, 'Glas nije dostupan');
    return res.blob();
  },
};
